import React, { FC, useEffect, useState } from 'react'
import _ from 'lodash'
import Isvg from 'react-inlinesvg'
import Moment from 'moment'
// components
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Checkbox } from 'primereact/checkbox';
// lib
import notifcationService from 'data/notification'
import propertyService from 'data/property'
import { formatDate } from 'data/lib/dates.ts'
import { propertyHasErrors } from 'data/lib/validation'
// styles
import { Label_s } from 'styles/app-styles'
import DataTable_s from 'components/table/data-table-styles'
import { CommercialIcon, HospitalityIcon, MixedUseIcon, MFRentIcon } from 'images/icons/sidebar'
import { ReportPropertyGrid_s, SelectAll_s } from "components/folder/reports/report-styles"
import folderService from 'data/folder'

interface FolderReportGrid {
  onChange: (selected: ReportGridProperty[]) => void
  propertyFilter?: (property: { type: string }) => boolean
  selectionMethod: string
  activeReport: number
}

export type ReportGridProperty = {
  analysisbegin: string
  code: string
  id: number
  reportbegin: string
  status: string
  term: number
}


const ReportPropertyGrid: FC<FolderReportGrid> = (props) => {
  const [properties, setProperties] = useState<object[]>([])
  const [folders, setFolders] = useState<object[]>([])
  const [data, updateData] = useState<ReportGridProperty[]>([])
  const currentFolder = _.last(JSON.parse(localStorage.getItem('folderPath')!))
  const [selected, updateSelected] = useState<ReportGridProperty[]>([])
  const propertyFilter = props.propertyFilter || (() => true)
  const filter = (p) => propertyFilter(p)
  const isSelectable = (p) => (p.status == 'Calculated' || p.status == 'Calculated with Warnings') && propertyFilter(p)
  const withSubfolders = props.selectionMethod === 'Current Folder and Subfolders'

  useEffect(() => {
    getFoldersAndProperties()
  }, [props.selectionMethod, props.activeReport])

  useEffect(() => {
    if (properties?.length > 0) updateData(getDefaultPropertyData(properties))
  }, [properties])

  useEffect(() => {
    // pre-select calculated properties when data available
    if (!properties.length || !data.length) return
    updateSelected(data.filter(isSelectable))
  }, [data, props.propertyFilter])

  useEffect(() => {
    props.onChange(selected)
  }, [selected])

  async function getFoldersAndProperties(){
    const folders = await folderService.fetchAllFolders();
    const folderIds: Array<number> = []
    if (!withSubfolders) folderIds.push(currentFolder)
    else {
      let todo = [{folderid: currentFolder}]
      while (todo.length) {
        const folder = todo.pop()
        folderIds.push(folder!.folderid)
        todo.push(...folders.filter(f => f['parentfolder'] == folder!.folderid))
      }
    }
    let properties = _.flatten(await Promise.all(folderIds.map(folderid => propertyService.fetchByFolderId(folderid))))
    if (withSubfolders) {
      const paths = _.transform(folders, (o, {folderid}) => o[folderid] = getFolderPath(folderid, [], folders), {})
      properties = _.sortBy(properties, [p => _.toLower(paths[p.folderid]), p => _.toLower(p.code)])
    } else {
      properties = _.orderBy(properties, [p => _.toLower(p.code)])
    }
    setFolders(folders)
    setProperties(_.filter(properties, filter))
  }

  function getDefaultPropertyData(properties) {
    return properties
      .map((property) => {
        const propertyCode = property.name
          ? `${property.code} - ${property.name}`
          : property.code

        let status = property.calcdetail ? property.calcdetail.status : 'Uncalculated'
        if (propertyHasErrors(property)) {
          if (status == 'Uncalculated') status = 'Warning: Review Inputs'
          else if (status == 'Calculated') status += ' with Warnings'
        }
        return {
          id: property.propertyid,
          code: propertyCode,
          analysisbegin: formatDate(property.analysisbegindate, 'MM/DD/YYYY'),
          reportbegin: formatDate(property.reportbegindate, 'MM/DD/YYYY'),
          term: property.term,
          type: property.type,
          status,
          folderid: property.folderid
        }
      })
  }

  function getFolderPath(folderid: number | nil, path:string[]=[], _folders:object[]|null=null) {
    _folders = _folders || folders
    let folder = _.find(_folders, {folderid})
    if (!folder || folderid == currentFolder) return path.join(' > ')
    return getFolderPath(folder.parentfolder, [folder.code, ...path])
  }

  const rowClassName = (rowData) => {
    return {'error-row' : _.find(folders, {folderid: rowData.folderid}).chartid !== _.find(folders, {folderid: currentFolder}).chartid || !rowData.status.startsWith('Calculated')};
  }

  const commercialIcon = <Isvg key={CommercialIcon} src={CommercialIcon} cacheGetRequests />;
  const hospitalityIcon = <Isvg key={HospitalityIcon} src={HospitalityIcon} cacheGetRequests style={{marginLeft: '7px'}} />;
  const mfIcon = <Isvg key={MFRentIcon} src={MFRentIcon} cacheGetRequests style={{height: '18px', marginLeft: '9px'}} />;
  const mixedUseIcon = <Isvg key={MixedUseIcon} src={MixedUseIcon} cacheRequests />;

  return (
    <ReportPropertyGrid_s>
      <Label_s>Selected Properties</Label_s>
      <DataTable_s style={{overflowY: 'visible'}}>
        <SelectAll_s>
          <Checkbox onChange={e => e.checked ? updateSelected(data) : updateSelected([])} checked={selected.length === data.length}></Checkbox>
        </SelectAll_s>
        <DataTable
          value={data}
          rowClassName={rowClassName}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          emptyMessage="No records found"
          selection={selected}
          onSelectionChange={(e) => e.value.length === data.length ? selected.length === data.length ? updateSelected([]) : updateSelected(e.value) : updateSelected(e.value)}
          footer={
            <Label_s style={{ float: 'left', marginTop: '-31px' }}>
              {selected.length} selected properties
            </Label_s>
          }>
          <Column selectionMode="multiple" />
          <Column header="Type" field="type" sortable={true} body={(rowData) => rowData.type === 'Multifamily' ? mfIcon : rowData.type === 'Hospitality' ? hospitalityIcon : rowData.type === 'Mixed-Use' ? mixedUseIcon : commercialIcon} style={{width: '70px'}} />
          { withSubfolders && <Column header="Subfolder" body={(rowData, props) => getFolderPath(rowData.folderid)} style={{width: '250px'}} /> }
          <Column field="code" header="Property" filter={true} sortable={true} style={{width: '150px'}}/>
          <Column
            field="analysisbegin"
            header="Analysis Begin"
            body={(rowData, props) => Moment(rowData.analysisbegin).format('MM/YYYY')}
            filter={true}
            sortable={true}
            style={{width: '130px'}}
          />
          <Column
            field="reportbegin"
            header="Report Begin"
            body={(rowData, props) => Moment(rowData.reportbegin).format('MM/YYYY')}
            filter={true}
            sortable={true}
            style={{width: '120px'}}
          />
          <Column field="term" header="Term" filter={true} sortable={true} style={{width: '80px'}} />
          <Column
            field="status"
            header="Calculation Status"
            filter={true}
            sortable={true}
            style={{width: '150px'}}
          />
        </DataTable>
      </DataTable_s>
    </ReportPropertyGrid_s>
  )
}

export default ReportPropertyGrid